import { CargoType } from '_metronic/helpers/enums'

/**
 * O mínimo que um vínculo precisa ter para as perguntas abaixo. Serve tanto para o `VinculoDto`
 * do usuário logado quanto para os vínculos devolvidos por outros endpoints (`FuncionarioView`,
 * `UsuarioDtoVinculo.vinculos`), que carregam os mesmos campos.
 */
export type VinculoBase = {
  cargo?: string | null
  medico?: boolean | null
  ativo?: boolean | null
  empresa?: { id?: unknown } | null
}

/**
 * Vínculo de profissional de saúde. `medico` vem de `Funcionario.isMedico()` no backend, que é
 * exatamente `cargo == MEDICO`; aceitar qualquer um dos dois cobre os dois jeitos que o código
 * historicamente perguntava isso.
 */
export const isVinculoMedico = (vinculo: VinculoBase): boolean =>
  vinculo.cargo === CargoType.Medico || !!vinculo.medico

export const isVinculoMedicoAtivo = (vinculo: VinculoBase): boolean =>
  !!vinculo.ativo && isVinculoMedico(vinculo)

/** Tem algum vínculo médico, ativo ou não (ex.: decidir se mostra campos de CRM no perfil). */
export const temVinculoMedico = (vinculos?: VinculoBase[] | null): boolean =>
  (vinculos ?? []).some(isVinculoMedico)

/** Tem algum vínculo médico ativo, em qualquer empresa. */
export const temVinculoMedicoAtivo = (vinculos?: VinculoBase[] | null): boolean =>
  (vinculos ?? []).some(isVinculoMedicoAtivo)

/** Vínculos médicos, ativos ou não. */
export const vinculosMedicos = <T extends VinculoBase>(vinculos?: T[] | null): T[] =>
  (vinculos ?? []).filter(isVinculoMedico)

/**
 * É médico ativo **nesta** empresa. A comparação é estrita de propósito: com `idEmpresa`
 * indefinido (tela ainda carregando) o resultado é `false`, nunca "qualquer empresa".
 */
export const temVinculoMedicoAtivoNaEmpresa = (
  vinculos: VinculoBase[] | null | undefined,
  idEmpresa: unknown
): boolean => (vinculos ?? []).some((v) => isVinculoMedicoAtivo(v) && v.empresa?.id === idEmpresa)
